import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  fetchBenchmarkData,
  fetchSegments,
  fetchParticipationReport,
  getBenchmarkRanges,
} from "@/services/benchmark.service";
import type {
  BenchmarkDataResponse,
  BenchmarkMetrics,
  BenchmarkParticipationReportResponse,
} from "@/services/benchmark.service";
import { cn } from "@/lib/utils";
import { formatSignedPercent } from "@/utils/numberFormat";

const WINDOW_LABELS: Record<WindowOption, string> = {
  "7d": "Últimos 7 dias",
  "14d": "Últimos 14 dias",
  "30d": "Últimos 30 dias",
};

type Scope = "global" | "personal";
type WindowOption = "7d" | "14d" | "30d";

function fmtReal(val: number | null | undefined): string {
  if (val == null) return "—";
  return (
    "R$ " +
    val.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function fmtRealCompact(val: number | null | undefined): string {
  if (val == null) return "—";
  if (Math.abs(val) >= 1000) return "R$ " + (val / 1000).toFixed(0) + "k";
  return fmtReal(val);
}

function fmtNum(val: number | null | undefined, casas = 0): string {
  if (val == null) return "—";
  return val.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function fmtNumCompact(val: number | null | undefined): string {
  if (val == null) return "—";
  if (Math.abs(val) >= 1000) return (val / 1000).toFixed(0) + "k";
  return fmtNum(val, 0);
}

function fmtPct(val: number | null | undefined): string {
  if (val == null) return "—";
  return fmtNum(val, 2) + "%";
}

function fmtCompact(val: number | null | undefined, formato: string): string {
  if (val == null) return "—";
  switch (formato) {
    case "real":
      return fmtRealCompact(val);
    case "pct":
      return fmtPct(val);
    case "num2":
      return fmtNum(val, 2);
    case "num_abs":
      return fmtNum(val, 0);
    case "num":
    default:
      return fmtNumCompact(val);
  }
}

function deltaPct(
  atual: number | null | undefined,
  anterior: number | null | undefined,
): number | null {
  if (atual == null || anterior == null || anterior === 0) return null;
  return ((atual - anterior) / Math.abs(anterior)) * 100;
}

function variacaoHtml(
  lojaVal: number | null | undefined,
  mediaVal: number | null | undefined,
  invertido: boolean,
): { text: string; cor: string } | null {
  if (lojaVal == null || mediaVal == null || mediaVal === 0) return null;
  const pct = ((lojaVal - mediaVal) / Math.abs(mediaVal)) * 100;
  const sinal = pct > 0 ? "▲" : pct < 0 ? "▼" : "—";
  const ehMelhor = invertido ? pct < 0 : pct > 0;
  const cor =
    pct === 0 ? "var(--text-muted)" : ehMelhor ? "#4ade80" : "#f87171";
  const direcao = pct > 0 ? "acima" : "abaixo";
  // Aqui o número sai em módulo de propósito: quem diz a direção é a palavra "acima"/"abaixo",
  // que sobrevive à cópia e à impressão — que é o que o item 34 pede. Repetir o sinal diria a
  // mesma coisa três vezes na mesma frase, contando a seta.
  return {
    text: `${sinal} ${Math.abs(pct).toFixed(0)}% ${direcao} da média atual`,
    cor,
  };
}

type Veredito = { label: string; cor: string; tip: string };

function bkVeredito(
  lojaDelta: number | null,
  mercadoDelta: number | null,
  invertido: boolean,
): Veredito | null {
  if (lojaDelta === null || mercadoDelta === null) return null;
  const lojaGanhou = invertido ? lojaDelta < -1 : lojaDelta > 1;
  const mercadoGanhou = invertido ? mercadoDelta < -1 : mercadoDelta > 1;
  if (Math.abs(lojaDelta) < 2 && Math.abs(mercadoDelta) < 2)
    return {
      label: "Estável",
      cor: "#94a3b8",
      tip: "Ambos variaram menos de 2% — sem movimento relevante no período.",
    };
  if (lojaGanhou && !mercadoGanhou)
    return {
      label: "Acima do mercado",
      cor: "#4ade80",
      tip: "Sua loja melhorou enquanto o mercado piorou ou ficou estável. Excelente resultado.",
    };
  if (lojaGanhou && mercadoGanhou) {
    const ld = invertido ? -lojaDelta : lojaDelta;
    const md = invertido ? -mercadoDelta : mercadoDelta;
    const diff = ld - md;
    if (diff > 1)
      return {
        label: "Acima do mercado",
        cor: "#4ade80",
        tip: "Ambos melhoraram, mas sua loja melhorou mais que o mercado.",
      };
    if (diff < -1)
      return {
        label: "Abaixo do mercado",
        cor: "#f59e0b",
        tip: "Ambos melhoraram, mas sua loja melhorou menos que o mercado.",
      };
    return {
      label: "Estável",
      cor: "#94a3b8",
      tip: "Variações equivalentes — sua loja e o mercado evoluíram na mesma proporção.",
    };
  }
  if (!lojaGanhou && mercadoGanhou)
    return {
      label: "Atenção",
      cor: "#f87171",
      tip: "Sua loja piorou enquanto o mercado melhorou. Vale investigar a causa.",
    };
  const quedaLoja = invertido ? lojaDelta : -lojaDelta;
  const quedaMercado = invertido ? mercadoDelta : -mercadoDelta;
  const diff = quedaLoja - quedaMercado;
  if (diff > 1)
    return {
      label: "Atenção",
      cor: "#f87171",
      tip: "Ambos pioraram, mas sua loja piorou mais que o mercado. Vale investigar a causa.",
    };
  if (diff < -1)
    return {
      label: "Resiliente",
      cor: "#60a5fa",
      tip: "Ambos pioraram, mas sua loja resistiu melhor que o mercado. Sinal positivo num cenário adverso.",
    };
  return {
    label: "Estável",
    cor: "#94a3b8",
    tip: "Variações equivalentes — sua loja e o mercado evoluíram na mesma proporção.",
  };
}

function deltaHtml(
  pct: number | null,
  invertido: boolean,
): { html: string; cor: string } {
  if (pct === null) return { html: "—", cor: "var(--text-muted)" };
  // A seta é reforço; quem carrega a direção é o sinal do número (item 34). O espaço separa
  // os dois — colados, `▲+12%` lê como um símbolo só.
  const sinal = pct > 0 ? "▲ " : pct < 0 ? "▼ " : "";
  const ehBom = invertido ? pct < 0 : pct > 0;
  const cor =
    Math.abs(pct) < 0.5 ? "var(--text-muted)" : ehBom ? "#4ade80" : "#f87171";
  return { html: `${sinal}${formatSignedPercent(pct, 0)}`, cor };
}

function formatarDataHora(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso.replace(" ", "T"));
  if (isNaN(d.getTime())) return null;
  return (
    d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }) +
    " às " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

function getMetric(
  m: BenchmarkMetrics | null | undefined,
  key: string,
): number | null | undefined {
  if (!m) return null;
  return (m as Record<string, number | null | undefined>)[key];
}

interface MetricCardCommon {
  label: string;
  sufixo?: string;
  icone?: string;
  key: string;
  formato: string;
  invertido: boolean;
  destaque?: boolean;
}

function MetricCard({
  metric,
  media,
  mediaAnt,
  loja,
  lojaAnt,
  rng,
  scope,
  window: windowOption,
  nomeLoja,
}: {
  metric: MetricCardCommon;
  media: BenchmarkMetrics | null;
  mediaAnt: BenchmarkMetrics | null;
  loja: BenchmarkMetrics | null;
  lojaAnt: BenchmarkMetrics | null;
  rng: Record<string, { min?: number | null; max?: number | null }>;
  scope: Scope;
  window: WindowOption;
  nomeLoja: string | null;
}) {
  const mediaVal = getMetric(media, metric.key);
  const lojaVal = getMetric(loja, metric.key);
  const mediaAntVal = getMetric(mediaAnt, metric.key);
  const lojaAntVal = getMetric(lojaAnt, metric.key);
  const range = rng[metric.key];
  const rangeVal =
    lojaVal != null &&
    range &&
    range.min != null &&
    range.max != null &&
    range.min !== range.max
      ? Math.max(
          0,
          Math.min(
            100,
            ((lojaVal - range.min) / (range.max - range.min)) * 100,
          ),
        )
      : null;
  const fmtVal = (v: number | null | undefined) =>
    fmtCompact(v, metric.formato);
  const varInfo =
    lojaVal != null && mediaVal != null
      ? variacaoHtml(lojaVal, mediaVal, metric.invertido)
      : null;
  const ld = deltaPct(lojaVal, lojaAntVal);
  const md = deltaPct(mediaVal, mediaAntVal);
  const veredito = bkVeredito(ld, md, metric.invertido);
  const sufixoHtml = metric.sufixo ? (
    <small className="font-normal opacity-70">{metric.sufixo}</small>
  ) : null;

  return (
    <div
      className={cn(
        "rounded-[10px] border p-[.9rem_1rem] flex flex-col gap-0",
        metric.destaque ? "bg-black/5 dark:bg-white/5" : "bg-card",
      )}
      style={{ borderColor: "hsl(var(--border))" }}
    >
      <span className="text-[.68rem] text-muted-foreground uppercase tracking-[.04em] font-bold mb-2 flex items-center gap-1">
        {metric.label}{" "}
        {metric.icone && (
          <img
            src={metric.icone}
            alt=""
            className="w-3 h-3 object-contain opacity-70"
          />
        )}
        {sufixoHtml}
      </span>
      <div className="flex gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          <div className="text-[.62rem] text-muted-foreground font-semibold uppercase tracking-[.03em] mb-[.1rem]">
            Mercado
          </div>
          <div className="text-[1.2rem] font-extrabold text-foreground truncate">
            {fmtVal(mediaVal)}
          </div>
        </div>
        {nomeLoja && loja && (
          <div className="flex-1 min-w-0">
            <div className="text-[.62rem] text-muted-foreground font-semibold uppercase tracking-[.03em] mb-[.1rem]">
              Sua loja
            </div>
            <div className="text-[1.2rem] font-extrabold text-primary truncate">
              {fmtVal(lojaVal)}
            </div>
          </div>
        )}
      </div>
      {varInfo && nomeLoja && lojaVal != null && (
        <span
          className="text-[.73rem] font-bold mb-2 block"
          style={{ color: varInfo.cor }}
        >
          {varInfo.text}
        </span>
      )}
      {rangeVal != null && nomeLoja && loja && (
        <div className="mb-2">
          <div className="relative h-[5px] rounded-[3px] my-1 bg-border">
            <div
              className="absolute left-0 top-0 h-full rounded-[3px]"
              style={{
                width: `${rangeVal}%`,
                background: "hsl(var(--primary))",
                opacity: 0.6,
              }}
            />
            <div
              className="absolute top-1/2 w-[10px] h-[10px] rounded-full border-2"
              style={{
                left: `${rangeVal}%`,
                transform: "translate(-50%, -50%)",
                background: "hsl(var(--primary))",
                borderColor: "hsl(var(--background))",
                boxShadow: "0 0 0 1px hsl(var(--primary))",
              }}
            />
          </div>
          <div className="flex justify-between text-[.6rem] text-muted-foreground opacity-70">
            <span>
              mín{" "}
              {range?.min != null
                ? Math.abs(range.min) >= 1000
                  ? (range.min / 1000).toFixed(0) + "k"
                  : range.min.toFixed(1).replace(/\.0$/, "")
                : "—"}
            </span>
            <span>
              máx{" "}
              {range?.max != null
                ? Math.abs(range.max) >= 1000
                  ? (range.max / 1000).toFixed(0) + "k"
                  : range.max.toFixed(1).replace(/\.0$/, "")
                : "—"}
            </span>
          </div>
        </div>
      )}
      {(mediaAntVal != null || lojaAntVal != null) && (
        <div
          className="mt-auto pt-2 border-t"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <div className="text-[.6rem] text-muted-foreground font-bold uppercase tracking-[.04em] mb-[.3rem]">
            {WINDOW_LABELS[windowOption]} — variação do período
          </div>
          <div className="flex gap-[.4rem] mb-[.3rem]">
            <div
              className="flex-1 rounded-[6px] p-[.3rem_.45rem] text-center"
              style={{ background: "rgba(255,255,255,.04)" }}
            >
              <span className="text-[.6rem] text-muted-foreground font-semibold uppercase block mb-[.1rem]">
                Mercado
              </span>
              <span
                className="text-[.82rem] font-extrabold"
                style={{ color: deltaHtml(md, metric.invertido).cor }}
              >
                {deltaHtml(md, metric.invertido).html}
              </span>
            </div>
            <div
              className="flex-1 rounded-[6px] p-[.3rem_.45rem] text-center"
              style={{ background: "rgba(255,255,255,.04)" }}
            >
              <span className="text-[.6rem] text-muted-foreground font-semibold uppercase block mb-[.1rem]">
                Sua loja
              </span>
              <span
                className="text-[.82rem] font-extrabold"
                style={{ color: deltaHtml(ld, metric.invertido).cor }}
              >
                {deltaHtml(ld, metric.invertido).html}
              </span>
            </div>
          </div>
          {veredito && (
            <div
              className="text-[.68rem] font-bold px-[.18rem_.5rem] rounded-[4px] text-center w-full"
              style={{ color: veredito.cor, background: `${veredito.cor}18` }}
              title={veredito.tip}
            >
              {veredito.label}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SecaoHeader({
  cor,
  nome,
  sub,
  icone,
}: {
  cor: string;
  nome: React.ReactNode;
  sub: string;
  icone?: string;
}) {
  return (
    <div className="flex items-center gap-[.65rem] mb-[.75rem]">
      <div
        className="w-[4px] h-[28px] rounded-[2px] shrink-0"
        style={{ background: cor }}
      />
      <div className="flex flex-col gap-[1px]">
        <span className="text-[.95rem] font-bold text-foreground flex items-center gap-[.35rem]">
          {icone && (
            <img src={icone} alt="" className="w-4 h-4 object-contain" />
          )}
          {nome}
        </span>
        <span className="text-[.68rem] text-muted-foreground">{sub}</span>
      </div>
      <div
        className="flex-1 h-[1px]"
        style={{ background: "hsl(var(--border))" }}
      />
    </div>
  );
}

const SEGMENTOS_PADRAO = [
  "Artesanato e personalizados",
  "Beleza e cosmeticos",
  "Calcados",
  "Casa e decoracao",
  "Infantil e bebe",
  "Joias e semijoias",
  "Moda",
  "Moda feminina",
  "Moda fitness",
  "Moda infantil",
  "Moda masculina",
  "Outro",
  "Perfumaria",
  "Presentes",
  "Religiao e artigos religiosos",
];

const VENDAS_METRICS: MetricCardCommon[] = [
  {
    label: "Receita média",
    sufixo: "Google Analytics",
    key: "receita",
    formato: "real",
    invertido: false,
    icone: "/analytics.png",
  },
  { label: "ROAS médio", key: "roas", formato: "num2", invertido: false },
  {
    label: "Ticket médio",
    key: "ticket_medio",
    formato: "real",
    invertido: false,
  },
  {
    label: "Taxa de conversão",
    key: "taxa_conversao",
    formato: "pct",
    invertido: false,
  },
  {
    label: "CPS médio",
    sufixo: "custo/sessão",
    key: "cps",
    formato: "real",
    invertido: true,
  },
  {
    label: "Sessões médias",
    sufixo: "Google Analytics",
    key: "sessoes",
    formato: "num",
    invertido: false,
    icone: "/analytics.png",
  },
];

const FUNIL_METRICS: MetricCardCommon[] = [
  {
    label: "Sessões",
    sufixo: "GA4",
    key: "sessoes",
    formato: "num",
    invertido: false,
  },
  {
    label: "Sessão → carrinho",
    key: "funil_sessao_carrinho",
    formato: "pct",
    invertido: false,
    destaque: true,
  },
  { label: "Carrinhos", key: "carrinhos", formato: "num", invertido: false },
  {
    label: "Carrinho → pedido",
    key: "funil_carrinho_pedido",
    formato: "pct",
    invertido: false,
    destaque: true,
  },
  { label: "Pedidos", key: "pedidos", formato: "num_abs", invertido: false },
  { label: "ROAS", key: "roas", formato: "num2", invertido: false },
];

const META_METRICS: MetricCardCommon[] = [
  { label: "Receita", key: "meta_receita", formato: "real", invertido: false },
  { label: "ROAS", key: "meta_roas", formato: "num2", invertido: false },
  {
    label: "Investimento",
    key: "meta_investimento",
    formato: "real",
    invertido: false,
  },
  { label: "CPC", key: "meta_cpc", formato: "real", invertido: true },
  {
    label: "CTR",
    sufixo: "link",
    key: "meta_ctr",
    formato: "pct",
    invertido: false,
  },
  { label: "CPM", key: "meta_cpm", formato: "real", invertido: true },
];

const GOOGLE_METRICS: MetricCardCommon[] = [
  {
    label: "Receita",
    key: "google_receita",
    formato: "real",
    invertido: false,
  },
  { label: "ROAS", key: "google_roas", formato: "num2", invertido: false },
  {
    label: "Investimento",
    key: "google_investimento",
    formato: "real",
    invertido: false,
  },
  { label: "CPC", key: "google_cpc", formato: "real", invertido: true },
  {
    label: "CTR",
    sufixo: "link",
    key: "google_ctr",
    formato: "pct",
    invertido: false,
  },
  { label: "CPA", key: "google_cpa", formato: "real", invertido: true },
];

function LegendBlock() {
  const items = [
    {
      cor: "#4ade80",
      label: "Acima do mercado",
      desc: "sua loja melhorou mais que o mercado, ou melhorou enquanto o mercado piorou",
    },
    {
      cor: "#f59e0b",
      label: "Abaixo do mercado",
      desc: "ambos melhoraram, mas sua loja melhorou menos",
    },
    {
      cor: "#60a5fa",
      label: "Resiliente",
      desc: "ambos pioraram, mas sua loja resistiu melhor que o mercado",
    },
    {
      cor: "#94a3b8",
      label: "Estável",
      desc: "variações equivalentes entre sua loja e o mercado (diferença ≤ 1%)",
    },
    {
      cor: "#f87171",
      label: "Atenção",
      desc: "sua loja piorou mais que o mercado, ou piorou enquanto o mercado melhorou",
    },
  ];
  return (
    <div
      className="mt-6 p-[1rem_1.2rem] rounded-[10px] border"
      style={{
        background: "rgba(255,255,255,.03)",
        borderColor: "hsl(var(--border))",
      }}
    >
      <p className="text-[.72rem] font-bold uppercase tracking-[.05em] text-muted-foreground mb-[.65rem]">
        Legenda — comparativo com período anterior
      </p>
      <div className="flex flex-wrap gap-[.5rem_.9rem]">
        {items.map((item) => (
          <span key={item.label} className="text-[.76rem]">
            <span className="font-extrabold" style={{ color: item.cor }}>
              ●
            </span>{" "}
            <b className="text-foreground">{item.label}</b>{" "}
            <span className="text-muted-foreground">— {item.desc}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const STATUS_COR: Record<string, string> = {
  ok: "#4ade80",
  sem_dados: "#f59e0b",
  erro: "#f87171",
  nao_configurado: "var(--text-muted)",
  pendente: "var(--text-muted)",
};

const STATUS_LABEL: Record<string, string> = {
  ok: "OK",
  sem_dados: "sem dados",
  erro: "falha na busca",
  nao_configurado: "não configurado",
  pendente: "...",
};

function statusBadge(status: string): React.ReactNode {
  return (
    <span
      style={{
        color: STATUS_COR[status] ?? "var(--text-muted)",
        fontWeight: 600,
      }}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

export default function BenchmarkPage() {
  const [scope, setScope] = useState<Scope>("global");
  const [segment, setSegment] = useState("__todos__");
  const [window, setWindow] = useState<WindowOption>("7d");
  const [segments, setSegments] = useState<string[]>(SEGMENTOS_PADRAO);
  const [data, setData] = useState<BenchmarkDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [hideMyAccount, setHideMyAccount] = useState(false);
  const [rankingExpandido, setRankingExpandido] = useState(false);

  const [participationData, setParticipationData] =
    useState<BenchmarkParticipationReportResponse | null>(null);
  const [participationLoading, setParticipationLoading] = useState(false);
  const [showParticipationModal, setShowParticipationModal] = useState(false);

  useEffect(() => {
    fetchSegments(scope).then((list) => {
      if (list.length > 0) setSegments(list);
    });
  }, [scope]);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    setRankingExpandido(false);

    fetchBenchmarkData({ scope, segment, window, hideMyAccount })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        if (d && d.sem_dados) {
          setError(null);
        } else if (!d) {
          setError("Erro ao carregar dados.");
        } else {
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Erro de conexão.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope, segment, window, hideMyAccount]);

  const handleScopeChange = useCallback((s: Scope) => {
    setScope(s);
    setSegment("__todos__");
    setWindow("7d");
  }, []);

  const m = data?.media ?? null;
  const l = data?.selected_account ?? null;
  const ma = data?.media_anterior ?? null;
  const la = data?.selected_account_anterior ?? null;
  const ranges = data ? getBenchmarkRanges(data) : {};
  const nomeLoja = data?.selected_account_name ?? null;
  const g = scope === "global";
  const atualizadoEm = data?.atualizado_em
    ? formatarDataHora(data?.atualizado_em)
    : null;

  const ranking = data?.ranking ?? [];
  const rankingMostrar = rankingExpandido ? ranking : ranking.slice(0, 10);

  async function handleOpenParticipation() {
    setParticipationLoading(true);
    setShowParticipationModal(true);
    try {
      const report = await fetchParticipationReport("7d");
      setParticipationData(report);
    } catch {
      setParticipationData(null);
    } finally {
      setParticipationLoading(false);
    }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Benchmark</h1>
        <p className="page-description">
          Compare o desempenho da sua loja com a média do mercado
        </p>
      </div>

      {scope === "personal" && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleOpenParticipation}
            className="text-[.78rem] font-bold rounded-[7px] px-[.85rem] py-[.38rem] border cursor-pointer transition-all duration-150 text-muted-foreground bg-transparent hover:border-primary hover:text-foreground"
            style={{ borderColor: "hsl(var(--border))" }}
          >
            Relatório de participação
          </button>
        </div>
      )}

      <Card className="overflow-hidden">
        {/* Scope bar */}
        <div
          className="flex w-full border-b"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <button
            type="button"
            onClick={() => handleScopeChange("global")}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-[2px] p-[.85rem_1rem] cursor-pointer border-none relative transition-all duration-150",
              scope === "global"
                ? "bg-primary/5 text-foreground"
                : "text-muted-foreground bg-card hover:bg-muted/30",
            )}
            style={
              scope === "global"
                ? { borderBottom: "2px solid hsl(var(--primary))" }
                : {}
            }
          >
            <span
              className={cn(
                "flex items-center gap-[.3rem]",
                scope === "global"
                  ? "text-[.95rem] font-extrabold"
                  : "text-[.85rem] font-bold",
              )}
            >
              🌐 Global
            </span>
            <span
              className={cn(
                "text-[.72rem]",
                scope === "global"
                  ? "text-foreground/70"
                  : "text-muted-foreground",
              )}
            >
              Todas as lojas da plataforma
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleScopeChange("personal")}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-[2px] p-[.85rem_1rem] cursor-pointer border-none relative transition-all duration-150 border-l",
              scope === "personal"
                ? "bg-primary/5 text-foreground"
                : "text-muted-foreground bg-card hover:bg-muted/30",
            )}
            style={
              scope === "personal"
                ? { borderBottom: "2px solid hsl(var(--primary))" }
                : scope === "global"
                  ? { borderLeft: "1px solid hsl(var(--border))" }
                  : {}
            }
          >
            <span
              className={cn(
                "flex items-center gap-[.3rem]",
                scope === "personal"
                  ? "text-[.95rem] font-extrabold"
                  : "text-[.85rem] font-bold",
              )}
            >
              🏢 Minhas lojas
            </span>
            <span
              className={cn(
                "text-[.72rem]",
                scope === "personal"
                  ? "text-foreground/70"
                  : "text-muted-foreground",
              )}
            >
              Só as lojas da sua conta
            </span>
          </button>
        </div>

        {/* Row 1: Segment chips + Ocultar minha loja */}
        <div className="flex items-center gap-[.75rem] p-[.65rem_1rem] flex-wrap">
          <div className="flex gap-[.3rem] flex-wrap flex-1">
            <button
              type="button"
              onClick={() => setSegment("__todos__")}
              className={cn(
                "text-[.75rem] font-semibold rounded-full px-[.75rem] py-[.28rem] cursor-pointer transition-all duration-150 border",
                segment === "__todos__"
                  ? "text-white border-primary bg-primary"
                  : "text-muted-foreground bg-transparent",
              )}
              style={
                segment !== "__todos__"
                  ? { borderColor: "hsl(var(--border))" }
                  : {}
              }
            >
              Todas as lojas
            </button>
            {segments.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSegment(s)}
                className={cn(
                  "text-[.75rem] font-semibold rounded-full px-[.75rem] py-[.28rem] cursor-pointer transition-all duration-150 border",
                  segment === s
                    ? "text-white border-primary bg-primary"
                    : "text-muted-foreground bg-transparent",
                )}
                style={
                  segment !== s ? { borderColor: "hsl(var(--border))" } : {}
                }
              >
                {s}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-[.35rem] text-[.75rem] text-muted-foreground cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={hideMyAccount}
              onChange={(e) => setHideMyAccount(e.target.checked)}
              className="accent-primary"
            />
            Ocultar minha loja
          </label>
        </div>

        {/* Row 2: Window selector + updated info */}
        <div
          className="flex items-center gap-[.75rem] p-[.65rem_1rem] flex-wrap border-t"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <div className="flex gap-[.25rem]">
            {(["7d", "14d", "30d"] as WindowOption[]).map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWindow(w)}
                className={cn(
                  "text-[.78rem] font-bold rounded-[7px] px-[.85rem] py-[.38rem] cursor-pointer transition-all duration-150 border",
                  window === w
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground bg-transparent",
                )}
                style={
                  window === w
                    ? { borderColor: "rgba(108,63,197,.3)" }
                    : { borderColor: "transparent" }
                }
              >
                {w === "7d" ? "7 dias" : w === "14d" ? "14 dias" : "30 dias"}
              </button>
            ))}
          </div>

          {atualizadoEm && (
            <div className="text-[.68rem] text-muted-foreground ml-auto">
              Dados atualizados em {atualizadoEm}.
            </div>
          )}
        </div>
      </Card>

      {!g && data && (
        <p className="text-[.82rem] text-muted-foreground mb-[1.1rem]">
          Segmento com <strong>{data.qtd_lojas_segmento ?? 0}</strong> loja(s)
          elegível(eis) da sua agência — média calculada com{" "}
          <strong>{data.qtd_lojas_media ?? 0}</strong>. Só entram lojas com GA4
          + (Meta ou Google Ads) configurados.
        </p>
      )}

      {loading && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[.85rem]">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-[10px] border p-[.9rem_1rem]">
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-6 w-20 mb-1" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/50 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {!loading && data && data.sem_dados && (
        <Card className="text-center p-[2.5rem]">
          <div className="text-2xl mb-2">📊</div>
          <div className="font-bold">
            Sem dados gravados ainda pra esse segmento/período
          </div>
          <div className="text-[.85rem] text-muted-foreground mt-1">
            Os dados são atualizados automaticamente todo dia de madrugada.
          </div>
        </Card>
      )}

      {!loading && data && !data.sem_dados && (
        <>
          {/* ── Métricas de vendas ── */}
          <SecaoHeader
            cor="#1D9E75"
            nome="📊 Métricas de vendas"
            sub="Receita, ROAS, ticket médio, conversão, CPS, sessões"
          />
          <div
            className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[.85rem] mb-6 p-4 rounded-[10px] border"
            style={{
              background: "rgba(108,63,197,.08)",
              borderColor: "rgba(108,63,197,.3)",
            }}
          >
            {VENDAS_METRICS.map((cfg) => (
              <MetricCard
                key={cfg.key}
                metric={cfg}
                media={m}
                mediaAnt={ma}
                loja={l}
                lojaAnt={la}
                rng={ranges}
                scope={scope}
                window={window}
                nomeLoja={nomeLoja}
              />
            ))}
          </div>

          {/* ── Funil de vendas ── */}
          <SecaoHeader
            cor="#534AB7"
            nome="🎯 Funil de vendas"
            sub="Sessões, carrinhos, pedidos e taxas de conversão"
          />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(155px,1fr))] gap-[.7rem] mb-6">
            {FUNIL_METRICS.map((cfg) => (
              <MetricCard
                key={cfg.key}
                metric={cfg}
                media={m}
                mediaAnt={ma}
                loja={l}
                lojaAnt={la}
                rng={ranges}
                scope={scope}
                window={window}
                nomeLoja={nomeLoja}
              />
            ))}
          </div>

          {/* ── Meta Ads ── */}
          <SecaoHeader
            cor="#185FA5"
            nome={
              <>
                <img
                  src="/metaAds.png"
                  alt=""
                  className="w-4 h-4 object-contain"
                />
                Meta Ads
              </>
            }
            sub="Receita, ROAS, investimento, CPC, CTR, CPM"
          />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(155px,1fr))] gap-[.7rem] mb-6">
            {META_METRICS.map((cfg) => (
              <MetricCard
                key={cfg.key}
                metric={cfg}
                media={m}
                mediaAnt={ma}
                loja={l}
                lojaAnt={la}
                rng={ranges}
                scope={scope}
                window={window}
                nomeLoja={nomeLoja}
              />
            ))}
          </div>

          {/* ── Google Ads ── */}
          <SecaoHeader
            cor="#A32D2D"
            nome={
              <>
                <img
                  src="/googleAds.png"
                  alt=""
                  className="w-4 h-4 object-contain"
                />
                Google Ads
              </>
            }
            sub="Receita, ROAS, investimento, CPC, CTR, CPA"
          />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(155px,1fr))] gap-[.7rem] mb-6">
            {GOOGLE_METRICS.map((cfg) => (
              <MetricCard
                key={cfg.key}
                metric={cfg}
                media={m}
                mediaAnt={ma}
                loja={l}
                lojaAnt={la}
                rng={ranges}
                scope={scope}
                window={window}
                nomeLoja={nomeLoja}
              />
            ))}
          </div>

          {/* ── Ranking ── */}
          {!g && ranking.length > 0 && (
            <Card className="mt-4">
              <div
                className="flex items-center justify-between px-5 py-3 border-b"
                style={{ borderColor: "hsl(var(--border))" }}
              >
                <div className="font-bold text-[.95rem]">
                  Ranking do segmento
                </div>
                <span
                  className="text-[.68rem] font-semibold px-[.45rem] py-[.1rem] rounded-full text-muted-foreground border"
                  style={{ borderColor: "hsl(var(--border))" }}
                >
                  Ordenado por receita (GA4)
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[.82rem]">
                  <thead>
                    <tr
                      className="border-b text-left text-muted-foreground text-[.72rem] font-bold uppercase tracking-[.04em]"
                      style={{ borderColor: "hsl(var(--border))" }}
                    >
                      <th className="px-5 py-[.55rem] w-[50px]">Pos.</th>
                      <th className="px-5 py-[.55rem]">Loja</th>
                      <th className="px-5 py-[.55rem] text-right">ROAS</th>
                      <th className="px-5 py-[.55rem] text-right">Receita</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankingMostrar.map((r, i) => (
                      <tr
                        key={r.nome}
                        className={cn(
                          "border-b",
                          r.is_current_account ? "font-bold" : "",
                        )}
                        style={{
                          borderColor: "hsl(var(--border))",
                          background: r.is_current_account
                            ? "rgba(108,63,197,.08)"
                            : undefined,
                        }}
                      >
                        <td className="px-5 py-[.5rem]">{i + 1}º</td>
                        <td className="px-5 py-[.5rem]">
                          {r.nome}
                          {r.is_current_account ? " (você)" : ""}
                        </td>
                        <td className="px-5 py-[.5rem] text-right">
                          {r.roas != null ? fmtNum(r.roas, 2) : "—"}
                        </td>
                        <td className="px-5 py-[.5rem] text-right">
                          {r.receita != null ? fmtRealCompact(r.receita) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ranking.length > 10 && (
                <button
                  type="button"
                  onClick={() => setRankingExpandido(!rankingExpandido)}
                  className="w-full text-[.78rem] font-bold py-[.55rem] border-t text-muted-foreground cursor-pointer hover:text-foreground transition-colors duration-150"
                  style={{ borderColor: "hsl(var(--border))" }}
                >
                  {rankingExpandido
                    ? "Mostrar só as 10 primeiras"
                    : `Ver todas as ${ranking.length} lojas do segmento`}
                </button>
              )}
            </Card>
          )}

          <LegendBlock />
        </>
      )}

      {/* ── Participation Report Modal ── */}
      <Dialog
        open={showParticipationModal}
        onOpenChange={setShowParticipationModal}
      >
        <DialogContent className="max-w-[780px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Relatório de participação</DialogTitle>
            <DialogDescription>
              Janela: <strong>7 dias</strong> — mostra todas as lojas,
              independente do segmento filtrado na tela.
            </DialogDescription>
          </DialogHeader>
          {participationLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Carregando...
            </div>
          ) : participationData && participationData.lojas.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[.82rem]">
                <thead>
                  <tr
                    className="border-b text-left text-muted-foreground text-[.72rem] font-bold uppercase tracking-[.04em]"
                    style={{ borderColor: "hsl(var(--border))" }}
                  >
                    <th className="px-4 py-[.55rem]">Loja</th>
                    <th className="px-4 py-[.55rem]">Segmento</th>
                    <th className="px-4 py-[.55rem]">GA4</th>
                    <th className="px-4 py-[.55rem]">Meta</th>
                    <th className="px-4 py-[.55rem]">Google</th>
                    <th className="px-4 py-[.55rem]">Participa?</th>
                    <th className="px-4 py-[.55rem]">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {participationData.lojas.map((loja) => (
                    <tr
                      key={loja.nome}
                      className="border-b"
                      style={{ borderColor: "hsl(var(--border))" }}
                    >
                      <td className="px-4 py-[.45rem] font-medium">
                        {loja.nome}
                      </td>
                      <td className="px-4 py-[.45rem] text-muted-foreground">
                        {loja.segmento || (
                          <span className="text-muted-foreground/60">
                            sem segmento
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-[.45rem]">
                        {statusBadge(loja.status_ga4)}
                      </td>
                      <td className="px-4 py-[.45rem]">
                        {statusBadge(loja.status_meta)}
                      </td>
                      <td className="px-4 py-[.45rem]">
                        {statusBadge(loja.status_google)}
                      </td>
                      <td className="px-4 py-[.45rem]">
                        {loja.participa ? (
                          <span className="text-[#4ade80] font-bold">Sim</span>
                        ) : (
                          <span className="text-[#f87171] font-bold">Não</span>
                        )}
                      </td>
                      <td className="px-4 py-[.45rem] text-[.74rem] text-muted-foreground">
                        {loja.motivo}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              Nenhuma loja encontrada.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
